import React from 'react';
import { useNavigate } from 'react-router-dom';

import { useTheme } from '../../theme/ThemeContext';
import ChatLogo from './ChatHeader/ChatLogo';
import ThemeToggleButton from './ChatHeader/ThemeToggleButton';
import { UserProfilePanel } from './UserProfile';
import { ROUTES } from '../../constants/routes';

/**
 * Shared branded header for admin full-page routes.
 *
 * Same visual as the AdminDashboard / ManageStaffPage header — ChatLogo on
 * the left, back button, theme toggle + user profile on the right. The CSS
 * classes (``admin-header``, ``admin-header-inner``, ``admin-header-left``,
 * ``admin-header-right``) are defined in ``AdminDashboard.css`` which is
 * globally loaded by the CRA bundle.
 *
 * Props:
 *   backTo     — route to navigate to on "← Back" click (default: /org)
 *   backLabel  — label for the back button (default: "← Dashboard")
 */
export default function AdminPageHeader({
  backTo = ROUTES.ORG,
  backLabel = '← Dashboard',
}) {
  const navigate = useNavigate();
  const { isDark } = useTheme();

  const headerBg = isDark ? 'rgba(14,14,15,0.85)' : 'rgba(255,255,255,0.85)';
  const borderColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const headerShadow = isDark ? '0 4px 24px rgba(0,0,0,0.4)' : '0 2px 12px rgba(0,0,0,0.06)';

  return (
    <header
      className="admin-header"
      data-testid="admin-page-header"
      style={{
        background: headerBg,
        borderBottom: `1px solid ${borderColor}`,
        boxShadow: headerShadow,
      }}
    >
      <div className="admin-header-inner">
        <div className="admin-header-left">
          <div className="logo-container">
            <ChatLogo isLoading={false} />
          </div>
          <button
            className="admin-back-btn"
            onClick={() => navigate(backTo)}
            style={{
              background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
              color: isDark ? '#5eead4' : '#0f766e',
              borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
              padding: '0.3rem 0.625rem',
              borderRadius: '0.5rem',
              border: '1px solid',
              fontSize: '0.75rem',
              cursor: 'pointer',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              transition: 'all 0.2s ease',
            }}
          >
            {backLabel}
          </button>
        </div>
        <div className="admin-header-right">
          <ThemeToggleButton />
          <UserProfilePanel />
        </div>
      </div>
    </header>
  );
}
